import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck2, CalendarClock, Download } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ViewingActions from "@/components/viewings/viewing_actions";
import { icsUrl, slotLabel, mockViewings } from "@/components/viewings/viewing_utils";

// Compact upcoming-viewings card for the lister dashboard and renter profile.
// When a proposal is waiting on YOU, the decision row (approve a time,
// decline, counter propose) renders right here so nobody has to dig into
// the chat to respond.
export default function ViewingsCard({ className = "" }) {
  const { user } = useAuth();
  const [viewings, setViewings] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/viewings/mine")
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.viewings || res.data?.items || [];
        setViewings(list.filter((v) => v.status !== "cancelled" && v.status !== "declined"));
      })
      .catch(() => {
        // Mock entries exist ONLY for user.demo sessions (backend-offline
        // dev). Real signed-in users never see fake viewings.
        if (alive) setViewings(user?.demo ? mockViewings() : []);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.demo]);

  if (!viewings || viewings.length === 0) return null;

  return (
    <section className={`rounded-3xl border border-night-line bg-night-surface p-6 shadow-hairline ${className}`}>
      <h2 className="font-semibold text-night-text">Viewings</h2>
      <p className="mt-0.5 text-xs text-night-sub">Your proposed and confirmed visits.</p>
      <ul className="mt-3 divide-y divide-night-line">
        {viewings.slice(0, 4).map((v) => {
          const myId = user?.id || user?._id;
          const awaitingMe =
            v.status === "proposed" && !v.demo && v.proposed_by && v.proposed_by !== myId;
          return (
            <li key={v.id} className="py-2.5">
              <div className="flex items-center gap-3">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                    v.status === "confirmed" ? "bg-night-tint text-brand-primary" : "bg-night-tint text-night-sub"
                  }`}
                >
                  {v.status === "confirmed" ? <CalendarCheck2 size={15} /> : <CalendarClock size={15} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-night-text">
                    {v.listing_title || v.listing?.title || "Viewing"}
                  </p>
                  <p className="truncate text-xs text-night-sub">
                    {v.status === "confirmed" && v.accepted_slot
                      ? `Confirmed · ${slotLabel(v.accepted_slot)}`
                      : awaitingMe
                        ? "Awaiting your reply"
                        : `Waiting on a time · ${(v.slots || []).length} option${(v.slots || []).length === 1 ? "" : "s"}`}
                  </p>
                </div>
                {v.status === "confirmed" ? (
                  <a
                    href={icsUrl(v.id)}
                    aria-label="Add viewing to calendar"
                    className="inline-flex items-center gap-1 rounded-full border border-night-line px-2.5 py-1 text-[11px] font-semibold text-night-text transition hover:border-brand-primary"
                  >
                    <Download size={11} /> .ics
                  </a>
                ) : v.match_id ? (
                  <Link
                    to={`/chat/${v.match_id}`}
                    className="rounded-full border border-night-line px-2.5 py-1 text-[11px] font-semibold text-night-text transition hover:border-brand-primary"
                  >
                    Open chat
                  </Link>
                ) : null}
              </div>
              {awaitingMe && (
                <div className="mt-2 pl-11">
                  <ViewingActions
                    viewing={v}
                    compact
                    onUpdate={(updated) =>
                      setViewings((list) =>
                        (list || [])
                          .map((x) => (x.id === updated?.id ? updated : x))
                          .filter((x) => x.status !== "cancelled" && x.status !== "declined")
                      )
                    }
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
